import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Edit, Trash2, Upload, X, Image as ImageIcon, Check, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, FileDown, FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { formatEuro } from '../utils/format';
import html2pdf from 'html2pdf.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Función para extraer solo calle y número de la dirección (sin planta, puerta, etc.)
const cleanAddressForMap = (direccion) => {
  if (!direccion) return '';
  
  // Buscar el punto donde empieza la información adicional y cortar ahí
  // Patrones que indican inicio de info adicional (Pl:, Pt:, Piso, Esc, etc.)
  const cutPatterns = [
    /\s+Pl\s*:/i,
    /\s+Pl\.\s*/i,
    /\s+Planta\s*/i,
    /\s+Pt\s*:/i,
    /\s+Pt\.\s*/i,
    /\s+Puerta\s*/i,
    /\s+Piso\s*/i,
    /\s+Esc\s*:/i,
    /\s+Esc\.\s*/i,
    /\s+Escalera\s*/i,
    /\s+Ent\s*:/i,
    /\s+Ent\.\s*/i,
    /\s+Entresuelo\s*/i,
    /\s+Bajo\s*/i,
    /\s+[ÁAa]tico\s*/i,
    /\s+Izq\s*/i,
    /\s+Izquierda\s*/i,
    /\s+Izda\s*/i,
    /\s+Dcha\s*/i,
    /\s+Dch\s*/i,
    /\s+Derecha\s*/i,
    /\s+Int\s*/i,
    /\s+Interior\s*/i,
    /\s+Ext\s*/i,
    /\s+Exterior\s*/i,
    /,\s*\d+\s*[º°ª]/i,  // Para casos como ", 2º"
  ];
  
  let cleaned = direccion;
  let minIndex = cleaned.length;
  
  cutPatterns.forEach(pattern => {
    const match = cleaned.match(pattern);
    if (match && match.index < minIndex) {
      minIndex = match.index;
    }
  });
  
  cleaned = cleaned.substring(0, minIndex).trim();
  
  // Limpiar comas finales
  cleaned = cleaned.replace(/\s*,\s*$/, '').trim();
  
  return cleaned;
};

const getEstadoStyle = (estado) => {
  switch (estado) {
    case 'Activo':
      return 'bg-sky-300 text-sky-900';
    case 'Ofertado':
      return 'bg-amber-400 text-amber-900';
    case 'Arras':
      return 'bg-orange-500 text-white';
    case 'En cartera':
      return 'bg-[#166534] text-white';
    case 'Reservado':
      return 'bg-red-600 text-white';
    case 'Vendido':
      return 'bg-black text-white';
    case 'Descartado':
      return 'bg-gray-400 text-white';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const pdfContentRef = useRef(null);
  const docInputRef = useRef(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfSections, setPdfSections] = useState({
    infoGeneral: true,
    caracteristicas: true,
    gastos: true,
    resumenEconomico: true,
    fotos: true,
    mapa: false
  });

  useEffect(() => {
    loadProperty();
  }, [id]);

  const loadProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar inmueble:', error);
      toast.error('Error al cargar el inmueble');
      setLoading(false);
    }
  };

  const openPdfDialog = () => setShowPdfDialog(true);

  const generatePDF = async () => {
    if (!property) return;
    
    setShowPdfDialog(false);
    setGeneratingPdf(true);
    toast.info('Generando PDF...');

    try {
      // Logo ES2 en base64
      const logoBase64 = 'data:image/jpeg;base64,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';

      // Crear contenedor temporal para el PDF - optimizado para 1 página A4
      const container = document.createElement('div');
      container.style.padding = '10px';
      container.style.fontFamily = 'Inter, sans-serif';
      container.style.maxWidth = '800px';
      container.style.background = 'white';
      container.style.fontSize = '10px';

      // Header con Logo y Ref, Dirección, Población
      container.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
          <div style="flex-shrink: 0; margin-right: 20px;">
            <img src="${logoBase64}" alt="ES2" style="height: 50px; width: auto;" />
          </div>
          <div style="flex: 1;">
            <div style="font-size: 9px; color: #666; margin-bottom: 2px;">Ref: ${property.ref || '-'}</div>
            <h1 style="font-size: 16px; font-weight: bold; margin: 0; font-family: Manrope, sans-serif;">
              ${property.direccion || '-'}
            </h1>
            <div style="font-size: 11px; color: #666; margin-top: 2px;">
              ${property.poblacion || '-'}
            </div>
          </div>
        </div>
        <hr style="border: none; border-top: 1px solid #e5e7eb; margin-bottom: 10px;" />
      `;

      // Información General
      if (pdfSections.infoGeneral) {
        container.innerHTML += `
          <div style="margin-bottom: 10px; background: #f8fafc; padding: 8px; border-radius: 6px;">
            <h2 style="font-size: 12px; font-weight: 600; margin-bottom: 6px; font-family: Manrope, sans-serif;">Información General</h2>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; font-size: 9px;">
              <div><span style="color: #666;">Dirección:</span><br/><strong>${property.direccion || '-'}</strong></div>
              <div><span style="color: #666;">Población:</span><br/><strong>${property.poblacion || '-'}</strong></div>
              <div><span style="color: #666;">CP:</span><br/><strong>${property.cp || '-'}</strong></div>
              <div><span style="color: #666;">Ref. Catastral:</span><br/><strong>${property.referencia_catastral || '-'}</strong></div>
              <div><span style="color: #666;">Fecha Entrada:</span><br/><strong>${property.fecha_entrada || '-'}</strong></div>
              <div><span style="color: #666;">Fuente:</span><br/><strong>${property.fuente || '-'}</strong></div>
            </div>
            ${property.observaciones ? `<div style="margin-top: 6px; font-size: 9px;"><span style="color: #666;">Observaciones:</span><br/><span>${property.observaciones}</span></div>` : ''}
          </div>
        `;
      }

      // Características
      if (pdfSections.caracteristicas) {
        container.innerHTML += `
          <div style="margin-bottom: 10px; background: #f8fafc; padding: 8px; border-radius: 6px;">
            <h2 style="font-size: 12px; font-weight: 600; margin-bottom: 6px; font-family: Manrope, sans-serif;">Características</h2>
            <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; font-size: 9px;">
              <div><span style="color: #666;">Superficie:</span><br/><strong>${property.superficie || 0} m²</strong></div>
              <div><span style="color: #666;">Habitaciones:</span><br/><strong>${property.habitaciones || 0}</strong></div>
              <div><span style="color: #666;">Baños:</span><br/><strong>${property.banos || 0}</strong></div>
              <div><span style="color: #666;">Ocupado:</span><br/><strong>${property.ocupado ? 'Sí' : 'No'}</strong></div>
              <div><span style="color: #666;">Ascensor:</span><br/><strong>${property.ascensor ? 'Sí' : 'No'}</strong></div>
            </div>
          </div>
        `;
      }

      // Gastos
      if (pdfSections.gastos && property.gastos && property.gastos.length > 0) {
        const gastosHtml = property.gastos.map(g => 
          `<tr><td style="padding: 3px; border-bottom: 1px solid #eee;">${g.descripcion}</td><td style="padding: 3px; border-bottom: 1px solid #eee; text-align: right;">${formatEuro(g.monto)}</td></tr>`
        ).join('');
        container.innerHTML += `
          <div style="margin-bottom: 10px;">
            <h2 style="font-size: 12px; font-weight: 600; margin-bottom: 6px; font-family: Manrope, sans-serif;">Gastos</h2>
            <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
              <thead><tr style="background: #f3f4f6;"><th style="padding: 4px; text-align: left;">Gasto</th><th style="padding: 4px; text-align: right;">Importe</th></tr></thead>
              <tbody>${gastosHtml}</tbody>
            </table>
          </div>
        `;
      }

      // Resumen Económico
      if (pdfSections.resumenEconomico) {
        const beneficioColor = property.beneficio >= 0 ? '#16a34a' : '#dc2626';
        container.innerHTML += `
          <div style="margin-bottom: 10px; background: #f8fafc; padding: 8px; border-radius: 6px;">
            <h2 style="font-size: 12px; font-weight: 600; margin-bottom: 8px; font-family: Manrope, sans-serif;">Resumen Económico</h2>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; font-size: 9px; margin-bottom: 8px;">
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid #e5e7eb;">
                <div style="color: #666; font-size: 8px;">Precio Compra</div>
                <div style="font-size: 11px; font-weight: bold;">${formatEuro(property.precio_compra)}</div>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid #e5e7eb;">
                <div style="color: #666; font-size: 8px;">Precio Venta</div>
                <div style="font-size: 11px; font-weight: bold;">${formatEuro(property.precio_venta)}</div>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid #e5e7eb;">
                <div style="color: #666; font-size: 8px;">Precio Referencia</div>
                <div style="font-size: 11px; font-weight: bold;">${formatEuro(property.precio_referencia)}</div>
              </div>
            </div>
            <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; font-size: 9px;">
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid #e5e7eb;">
                <div style="color: #666; font-size: 8px;">ITP (10%)</div>
                <div style="font-size: 10px; font-weight: bold; color: #1f3d2a;">${formatEuro(property.itp)}</div>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid #e5e7eb;">
                <div style="color: #666; font-size: 8px;">Gastos Totales</div>
                <div style="font-size: 10px; font-weight: bold;">${formatEuro(property.gastos_totales)}</div>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid #e5e7eb;">
                <div style="color: #666; font-size: 8px;">Beneficio</div>
                <div style="font-size: 10px; font-weight: bold; color: ${beneficioColor};">${formatEuro(property.beneficio)}</div>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid #e5e7eb;">
                <div style="color: #666; font-size: 8px;">% Beneficio</div>
                <div style="font-size: 10px; font-weight: bold; color: ${beneficioColor};">${property.porcentaje_beneficio?.toFixed(2) || 0}%</div>
              </div>
              <div style="background: white; padding: 6px; border-radius: 4px; border: 1px solid #e5e7eb;">
                <div style="color: #666; font-size: 8px;">Días Cartera</div>
                <div style="font-size: 10px; font-weight: bold;">${property.dias_en_cartera !== null && property.dias_en_cartera !== undefined ? property.dias_en_cartera : '-'}</div>
              </div>
            </div>
          </div>
        `;
      }

      // Documentos Adjuntos
      if (property.documentos && property.documentos.length > 0) {
        const docsHtml = property.documentos.map(doc => {
          const nombre = doc.descripcion || doc.original_name || doc.filename || doc.name || 'Sin nombre';
          return `<li style="margin-bottom: 3px;">${nombre}</li>`;
        }).join('');
        container.innerHTML += `
          <div style="margin-bottom: 10px; background: #f8fafc; padding: 8px; border-radius: 6px;">
            <h2 style="font-size: 12px; font-weight: 600; margin-bottom: 6px; font-family: Manrope, sans-serif;">Documentos Adjuntos</h2>
            <ul style="font-size: 9px; margin: 0; padding-left: 16px;">
              ${docsHtml}
            </ul>
          </div>
        `;
      }

      // Footer
      container.innerHTML += `
        <div style="margin-top: 15px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 8px; color: #999; text-align: center;">
          ES2 Espacios Singulares - Generado el ${new Date().toLocaleDateString('es-ES')}
        </div>
      `;

      document.body.appendChild(container);

      const opt = {
        margin: [5, 5, 5, 5],
        filename: `inmueble_${property.ref || property.id}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(container).save();
      document.body.removeChild(container);
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast.error('Error al generar el PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar este inmueble?')) return;

    try {
      await axios.delete(`${API}/properties/${id}`);
      toast.success('Inmueble eliminado correctamente');
      navigate('/properties');
    } catch (error) {
      console.error('Error al eliminar inmueble:', error);
      toast.error('Error al eliminar el inmueble');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/properties/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Foto subida correctamente');
      loadProperty();
    } catch (error) {
      console.error('Error al subir foto:', error);
      toast.error('Error al subir la foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async (filename) => {
    try {
      await axios.delete(`${API}/properties/${id}/photos/${filename}`);
      toast.success('Foto eliminada');
      loadProperty();
    } catch (error) {
      console.error('Error al eliminar foto:', error);
      toast.error('Error al eliminar la foto');
    }
  };

  const handlePhotoDeleteFromLightbox = async () => {
    if (property.fotos && property.fotos.length > 0) {
      const filename = property.fotos[currentImageIndex];
      try {
        await axios.delete(`${API}/properties/${id}/photos/${filename}`);
        toast.success('Foto eliminada');
        setLightboxOpen(false);
        loadProperty();
      } catch (error) {
        console.error('Error al eliminar foto:', error);
        toast.error('Error al eliminar la foto');
      }
    }
  };

  // ============================================
  // MÓDULO DE DOCUMENTOS
  // ============================================

  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF');
      return;
    }
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/properties/${id}/documents`, formData);
      toast.success('Documento subido');
      loadProperty();
    } catch (error) {
      toast.error('Error al subir documento');
    } finally {
      setUploadingDoc(false);
      // Limpiar el input para permitir subir el mismo archivo otra vez
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    const docId = doc.id || doc.public_id;
    try {
      await axios.delete(`${API}/properties/${id}/documents/${encodeURIComponent(docId)}`);
      toast.success('Documento eliminado');
      loadProperty();
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  // ============================================

  const openImage = (foto, index) => {
    setLightboxImage(`${BACKEND_URL}/api/photos/${foto}`);
    setCurrentImageIndex(index);
    setImageZoom(1);
    setLightboxOpen(true);
  };

  const goToPreviousImage = () => {
    if (property.fotos && property.fotos.length > 0) {
      const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : property.fotos.length - 1;
      setCurrentImageIndex(newIndex);
      setLightboxImage(`${BACKEND_URL}/api/photos/${property.fotos[newIndex]}`);
      setImageZoom(1);
    }
  };

  const goToNextImage = () => {
    if (property.fotos && property.fotos.length > 0) {
      const newIndex = currentImageIndex < property.fotos.length - 1 ? currentImageIndex + 1 : 0;
      setCurrentImageIndex(newIndex);
      setLightboxImage(`${BACKEND_URL}/api/photos/${property.fotos[newIndex]}`);
      setImageZoom(1);
    }
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setImageZoom(1);
  };

  if (loading) {
    return <div className="p-4 text-center text-sm">Cargando...</div>;
  }

  if (!property) {
    return <div className="p-4 text-center text-sm">Inmueble no encontrado</div>;
  }

  return (
    <div className="p-3 md:p-4 max-w-7xl mx-auto space-y-4">
      {/* Header con botones - Reorganizado para móvil */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/properties')}
            data-testid="back-btn"
            className="flex-shrink-0"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg md:text-xl font-bold tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {property.direccion}
            </h1>
            {property.poblacion && (
              <p className="text-xs md:text-sm text-muted-foreground truncate">{property.poblacion}</p>
            )}
            {property.ref && (
              <p className="text-xs text-muted-foreground mt-0.5">Ref: {property.ref}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap print:hidden">
          <span 
            data-testid="property-status"
            className={`text-xs md:text-sm px-2 md:px-3 py-1 rounded-full font-medium ${getEstadoStyle(property.estado)}`}
          >
            {property.estado || 'Activo'}
          </span>
          <div className="flex items-center gap-1.5">
            <Button 
              variant="outline"
              size="sm"
              onClick={openPdfDialog}
              disabled={generatingPdf}
              data-testid="pdf-btn"
              className="h-8 px-2 md:px-3"
            >
              <FileDown className="mr-1" size={14} />
              <span className="hidden sm:inline">{generatingPdf ? '...' : 'PDF'}</span>
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => navigate(`/properties/edit/${id}`)}
              data-testid="edit-btn"
              className="h-8 px-2 md:px-3"
            >
              <Edit className="mr-1" size={14} />
              <span className="hidden sm:inline">Editar</span>
            </Button>
            <Button 
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              data-testid="delete-btn"
              className="h-8 px-2 md:px-3"
            >
              <Trash2 className="mr-1" size={14} />
              <span className="hidden sm:inline">Eliminar</span>
            </Button>
          </div>
        </div>
      </div>

      <div ref={pdfContentRef} className="pdf-content">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <div className="lg:col-span-2 space-y-3 md:space-y-4">
          <Card className="p-3 md:p-4">
            <h2 className="text-sm font-semibold mb-2 md:mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Información General</h2>
            
            {/* Fila 1: ID, Dirección completa */}
            <div className="grid grid-cols-6 gap-2 md:gap-3 mb-2">
              <div className="col-span-1">
                <p className="text-[10px] text-muted-foreground">ID</p>
                <p className="text-xs md:text-sm font-bold text-primary">{property.ref || '-'}</p>
              </div>
              <div className="col-span-5">
                <p className="text-[10px] text-muted-foreground">Dirección</p>
                <p className="text-xs md:text-sm font-medium">{property.direccion}</p>
              </div>
            </div>

            {/* Fila 2: CP, Población, Ref. Catastral */}
            <div className="grid grid-cols-3 gap-2 md:gap-3 mb-2">
              <div>
                <p className="text-[10px] text-muted-foreground">CP</p>
                <p className="text-xs md:text-sm font-medium">{property.cp || '-'}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Población</p>
                <p className="text-xs md:text-sm font-medium">{property.poblacion || '-'}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Ref. Catastral</p>
                <p className="text-xs md:text-sm font-medium truncate">{property.referencia_catastral || '-'}</p>
              </div>
            </div>

            {/* Fila 3: Fuente, Comercial */}
            <div className="grid grid-cols-3 gap-2 md:gap-3 mb-2">
              <div>
                <p className="text-[10px] text-muted-foreground">Fuente</p>
                <p className="text-xs md:text-sm font-medium">{property.fuente || '-'}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Comercial</p>
                <p className="text-xs md:text-sm font-medium">{property.comercial || '-'}</p>
              </div>
            </div>

            {/* Fila 4: Fechas */}
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              <div>
                <p className="text-[10px] text-muted-foreground">Fecha Entrada</p>
                <p className="text-xs md:text-sm font-medium">{property.fecha_entrada ? new Date(property.fecha_entrada).toLocaleDateString('es-ES') : '-'}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Fecha Compra</p>
                <p className="text-xs md:text-sm font-medium">{property.fecha_compra ? new Date(property.fecha_compra).toLocaleDateString('es-ES') : '-'}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Fecha Venta</p>
                <p className="text-xs md:text-sm font-medium">{property.fecha_venta ? new Date(property.fecha_venta).toLocaleDateString('es-ES') : '-'}</p>
              </div>
            </div>

            {property.observaciones && (
              <>
                <Separator className="my-2" />
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Observaciones</p>
                  <p className="text-xs whitespace-pre-wrap">{property.observaciones}</p>
                </div>
              </>
            )}
          </Card>

          <Card className="p-3 md:p-4">
            <h2 className="text-sm font-semibold mb-2 md:mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Características</h2>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 md:gap-3">
              <div>
                <p className="text-[10px] text-muted-foreground">Superficie</p>
                <p className="text-xs md:text-sm font-semibold">{property.superficie} m²</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Habitaciones</p>
                <p className="text-xs md:text-sm font-semibold">{property.habitaciones}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Baños</p>
                <p className="text-xs md:text-sm font-semibold">{property.banos}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Ocupado</p>
                <p className="text-xs md:text-sm font-semibold flex items-center gap-1">
                  {property.ocupado ? <Check className="text-green-600" size={12} /> : ''}
                  {property.ocupado ? 'Sí' : 'No'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Ascensor</p>
                <p className="text-xs md:text-sm font-semibold flex items-center gap-1">
                  {property.ascensor ? <Check className="text-green-600" size={12} /> : ''}
                  {property.ascensor ? 'Sí' : 'No'}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-3 md:p-4">
            <h2 className="text-sm font-semibold mb-2 md:mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Resumen Económico</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3 mb-3">
              <div>
                <p className="text-[10px] text-muted-foreground">Precio Compra</p>
                <p className="text-xs md:text-sm font-bold text-primary">{formatEuro(property.precio_compra)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Precio Venta</p>
                <p className="text-xs md:text-sm font-bold">{formatEuro(property.precio_venta)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Precio Ref.</p>
                <p className="text-xs md:text-sm font-bold">{formatEuro(property.precio_referencia)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">ITP (10%)</p>
                <p className="text-xs md:text-sm font-bold">{formatEuro(property.itp)}</p>
              </div>
            </div>

            {property.gastos && property.gastos.length > 0 && (
              <>
                <Separator className="my-3" />
                <h3 className="text-xs font-semibold mb-2">Gastos</h3>
                <div className="space-y-1">
                  {property.gastos.map((gasto, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <p className="text-xs">{gasto.descripcion}</p>
                      <p className="text-xs font-medium">{formatEuro(gasto.monto)}</p>
                    </div>
                  ))}
                  <Separator className="my-1" />
                  <div className="flex justify-between items-center">
                    <p className="text-xs font-semibold">Gastos Totales</p>
                    <p className="font-bold text-sm">{formatEuro(property.gastos_totales)}</p>
                  </div>
                </div>
                <Separator className="my-3" />
              </>
            )}

            <div className="grid grid-cols-5 gap-2 md:gap-3 pt-2">
              <div className="bg-blue-50 p-2 md:p-3 rounded-lg">
                <p className="text-[10px] text-muted-foreground">P. Compra + Gastos</p>
                <p className="text-sm md:text-lg font-bold text-gray-800">
                  {formatEuro((property.precio_compra || 0) + (property.itp || 0) + (property.gastos_totales || 0))}
                </p>
              </div>
              <div className="bg-green-50 p-2 md:p-3 rounded-lg">
                <p className="text-[10px] text-muted-foreground">Beneficio</p>
                <p className={`text-sm md:text-lg font-bold ${property.beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatEuro(property.beneficio)}
                </p>
              </div>
              <div className="bg-green-50 p-2 md:p-3 rounded-lg">
                <p className="text-[10px] text-muted-foreground">% Beneficio</p>
                <p className={`text-sm md:text-lg font-bold ${property.porcentaje_beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {property.porcentaje_beneficio?.toFixed(2) || 0}%
                </p>
              </div>
              <div className="bg-green-50 p-2 md:p-3 rounded-lg">
                <p className="text-[10px] text-muted-foreground">% Anual</p>
                <p className={`text-sm md:text-lg font-bold ${property.porcentaje_beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {property.dias_en_cartera && property.dias_en_cartera > 0 
                    ? ((property.porcentaje_beneficio || 0) * 365 / property.dias_en_cartera).toFixed(2) + '%'
                    : '-'}
                </p>
              </div>
              <div className="bg-amber-50 p-2 md:p-3 rounded-lg">
                <p className="text-[10px] text-muted-foreground">Días Cartera</p>
                <p className="text-sm md:text-lg font-bold text-gray-800">
                  {property.dias_en_cartera !== null && property.dias_en_cartera !== undefined ? property.dias_en_cartera : '-'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Columna lateral - Galería y Mapa */}
        <div className="flex flex-col space-y-3 md:space-y-4 h-full">
          <Card className="p-3 md:p-4">
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <h2 className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Galería</h2>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                  data-testid="photo-upload-input"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => document.getElementById('photo-upload').click()}
                  disabled={uploadingPhoto}
                  data-testid="upload-photo-btn"
                >
                  <Upload className="mr-1" size={12} />
                  {uploadingPhoto ? '...' : 'Subir'}
                </Button>
              </div>
            </div>
            {property.fotos && property.fotos.length > 0 ? (
              <div>
                <div className="relative group">
                  <img
                    src={`${BACKEND_URL}/api/photos/${property.fotos[0]}`}
                    alt="Foto principal"
                    className="w-full h-32 md:h-40 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ imageOrientation: 'from-image' }}
                    onClick={() => openImage(property.fotos[0], 0)}
                    data-testid="photo-0"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePhotoDelete(property.fotos[0]);
                    }}
                    data-testid="delete-photo-0"
                  >
                    <Trash2 size={12} />
                  </Button>
                  {property.fotos.length > 1 && (
                    <div className="absolute bottom-1 right-1 bg-black/70 text-white px-2 py-0.5 rounded-full text-xs">
                      +{property.fotos.length - 1} fotos
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 md:py-6 text-muted-foreground">
                <ImageIcon className="mx-auto mb-2" size={28} />
                <p className="text-xs">No hay fotos</p>
              </div>
            )}
          </Card>

          <Card className="p-3 md:p-4 flex-1 flex flex-col">
            <h2 className="text-sm font-semibold mb-2 md:mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Ubicación</h2>
            <div className="w-full flex-1 min-h-[200px] rounded-lg overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(cleanAddressForMap(property.direccion) + ', ' + property.poblacion)}`}
                data-testid="google-map"
              />
            </div>
          </Card>
        </div>
      </div>
      </div> {/* Cierre de pdf-content */}

      {/* SECCIÓN DE DOCUMENTOS */}
      <Card className="p-3 md:p-4 print:hidden">
        <div className="flex items-center justify-between mb-2 md:mb-3">
          <h2 className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Documentos</h2>
          <div>
            <input
              ref={docInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleDocUpload}
              className="hidden"
            />
            <Button 
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => docInputRef.current?.click()}
              disabled={uploadingDoc}
            >
              <Upload className="mr-1" size={12} />
              {uploadingDoc ? '...' : 'Subir Doc'}
            </Button>
          </div>
        </div>

        {/* Lista de documentos */}
        {property.documentos && property.documentos.length > 0 ? (
          <div className="space-y-2">
            {property.documentos.map((doc, index) => {
              const docId = doc.id || doc.public_id;
              return (
                <div 
                  key={docId || index} 
                  className="flex items-center justify-between p-2 border rounded-lg"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="text-primary flex-shrink-0" size={16} />
                    <span className="text-xs truncate">{doc.descripcion || 'Documento'}</span>
                  </div>
                  <div className="flex gap-1">
                    <a
                      href={`${API}/documents/${id}/${encodeURIComponent(docId)}`}
                      className="inline-flex items-center justify-center h-6 w-6 p-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
                      title="Descargar"
                    >
                      <Download size={12} />
                    </a>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => deleteDocument(doc)}
                      title="Eliminar"
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-2">No hay documentos</p>
        )}
      </Card>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl" data-testid="lightbox-dialog">
          <DialogHeader>
            <DialogTitle>
              Vista de Imagen {property?.fotos?.length > 1 && `(${currentImageIndex + 1} de ${property.fotos.length})`}
            </DialogTitle>
          </DialogHeader>
          <div className="relative overflow-hidden">
            <div className="flex items-center justify-center min-h-[400px] max-h-[600px] overflow-auto bg-muted/30 rounded-lg">
              <img
                src={lightboxImage}
                alt="Vista ampliada"
                style={{ 
                  transform: `scale(${imageZoom})`,
                  transition: 'transform 0.2s',
                  maxWidth: '100%',
                  height: 'auto'
                }}
                className="rounded-lg"
                data-testid="lightbox-image"
              />
            </div>
            
            {property?.fotos?.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-background/80 hover:bg-background"
                  onClick={goToPreviousImage}
                  data-testid="prev-image-btn"
                >
                  <ChevronLeft size={24} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-background/80 hover:bg-background"
                  onClick={goToNextImage}
                  data-testid="next-image-btn"
                >
                  <ChevronRight size={24} />
                </Button>
              </>
            )}
          </div>
          <DialogFooter className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleZoomOut}
                disabled={imageZoom <= 0.5}
                data-testid="zoom-out-btn"
              >
                <ZoomOut size={16} className="mr-1" />
                -
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={resetZoom}
                data-testid="reset-zoom-btn"
              >
                <Maximize2 size={16} className="mr-1" />
                {Math.round(imageZoom * 100)}%
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleZoomIn}
                disabled={imageZoom >= 3}
                data-testid="zoom-in-btn"
              >
                <ZoomIn size={16} className="mr-1" />
                +
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="destructive" 
                size="sm"
                onClick={handlePhotoDeleteFromLightbox}
                data-testid="lightbox-delete-btn"
              >
                <Trash2 size={16} className="mr-1" />
                Eliminar
              </Button>
              <Button onClick={() => setLightboxOpen(false)} data-testid="lightbox-close-btn">
                Cerrar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de selección de secciones para PDF */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent data-testid="pdf-sections-dialog">
          <DialogHeader>
            <DialogTitle>Generar PDF de Ficha</DialogTitle>
            <DialogDescription>
              Selecciona las secciones que deseas incluir en el PDF
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pdfSections.infoGeneral}
                  onChange={(e) => setPdfSections(prev => ({...prev, infoGeneral: e.target.checked}))}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm">Información General</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pdfSections.caracteristicas}
                  onChange={(e) => setPdfSections(prev => ({...prev, caracteristicas: e.target.checked}))}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm">Características</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pdfSections.gastos}
                  onChange={(e) => setPdfSections(prev => ({...prev, gastos: e.target.checked}))}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm">Desglose de Gastos</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pdfSections.resumenEconomico}
                  onChange={(e) => setPdfSections(prev => ({...prev, resumenEconomico: e.target.checked}))}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm">Resumen Económico</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPdfDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={generatePDF} disabled={generatingPdf}>
              <FileDown className="mr-2" size={16} />
              Generar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}